/**
 * Central vital/category metadata for the OOR Report module (SCRUM-134 / SCRUM-137).
 *
 * Single source of truth for the legend cards, the per-vital category bars and
 * their hover tooltips, so all three always agree. Colors mirror the
 * patient-dashboard HealthChart bar; labels and ranges are the US clinical
 * standards (client direction) and the KEYS match the backend's canonical
 * sub-category keys exactly (Category Report / List Report APIs), so the
 * adapters can map by key with no fuzzy matching. Temperature/pulse-ox/weight
 * ranges match the backend's classification cutoffs.
 */

export const CATEGORY_COLORS = {
  blue: "#3b82f6",
  green: "#10b981",
  lime: "#84cc16",
  amber: "#f59e0b",
  red: "#ef4444",
  darkRed: "#7f1d1d",
  grey: "#9ca3af", // fallback for unclassified / unrecognized backend categories
};

export const VITALS = [
  {
    key: "blood_pressure",
    label: "Blood pressure",
    categories: [
      { key: "hypotensive", label: "Hypotensive", range: "<90 or <60", color: CATEGORY_COLORS.blue },
      { key: "normal", label: "Normal", range: "90–120 and <80", color: CATEGORY_COLORS.green },
      { key: "elevated", label: "Elevated", range: "120–129 and <80", color: CATEGORY_COLORS.lime },
      { key: "stage_1_hypertension", label: "Stage 1 Hypertension", range: "130–139 or 80–89", color: CATEGORY_COLORS.amber },
      { key: "stage_2_hypertension", label: "Stage 2 Hypertension", range: "≥140 or ≥90", color: CATEGORY_COLORS.red },
      { key: "hypertensive_crisis", label: "Hypertensive Crisis", range: ">180 and/or >120", color: CATEGORY_COLORS.darkRed },
    ],
  },
  {
    key: "temperature",
    label: "Temperature",
    categories: [
      { key: "hypothermia", label: "Hypothermia", range: "<95.0°F", color: CATEGORY_COLORS.darkRed },
      { key: "low", label: "Low", range: "95.0–97.0°F", color: CATEGORY_COLORS.blue },
      { key: "normal", label: "Normal", range: "97.0–99.0°F", color: CATEGORY_COLORS.green },
      { key: "low_grade_fever", label: "Low grade fever", range: "99.0–100.4°F", color: CATEGORY_COLORS.amber },
      { key: "fever", label: "Fever", range: "≥100.4°F", color: CATEGORY_COLORS.red },
    ],
  },
  {
    key: "heart_rate",
    label: "Heart rate",
    categories: [
      { key: "bradycardia", label: "Bradycardia", range: "<60 BPM", color: CATEGORY_COLORS.amber },
      { key: "normal", label: "Normal", range: "60–100 BPM", color: CATEGORY_COLORS.green },
      { key: "tachycardia", label: "Tachycardia", range: ">100 BPM", color: CATEGORY_COLORS.red },
    ],
  },
  {
    key: "weight",
    label: "Weight",
    categories: [
      { key: "underweight", label: "Underweight", range: "BMI <18.5", color: CATEGORY_COLORS.blue },
      { key: "normal", label: "Normal", range: "BMI 18.5–24.9", color: CATEGORY_COLORS.green },
      { key: "overweight", label: "Overweight", range: "BMI 25.0–29.9", color: CATEGORY_COLORS.amber },
      { key: "obese", label: "Obese", range: "BMI ≥30.0", color: CATEGORY_COLORS.red },
    ],
  },
  {
    key: "blood_glucose",
    label: "Glucose",
    categories: [
      { key: "normal", label: "Normal", range: "<100 mg/dL", color: CATEGORY_COLORS.green },
      { key: "prediabetes", label: "Prediabetes", range: "100–125 mg/dL", color: CATEGORY_COLORS.amber },
      { key: "diabetes", label: "Diabetes", range: "≥126 mg/dL", color: CATEGORY_COLORS.red },
    ],
  },
  {
    key: "pulse_ox",
    label: "Pulse Oximetry",
    categories: [
      { key: "normal", label: "Normal", range: "95%–100%", color: CATEGORY_COLORS.green },
      { key: "mild_hypoxia", label: "Mild Hypoxia", range: "90%–94%", color: CATEGORY_COLORS.lime },
      { key: "moderate_hypoxia", label: "Moderate Hypoxia", range: "85%–89%", color: CATEGORY_COLORS.amber },
      { key: "severe_hypoxia", label: "Severe Hypoxia", range: "<85%", color: CATEGORY_COLORS.red },
    ],
  },
];

export const VITALS_BY_KEY = VITALS.reduce((acc, v) => {
  acc[v.key] = v;
  return acc;
}, {});

const UNCLASSIFIED_META = {
  key: "unclassified",
  label: "Unclassified",
  color: CATEGORY_COLORS.grey,
};

/**
 * Resolve a backend sub-category KEY (e.g. "stage_2_hypertension") to its
 * { key, label, color } for a vital. The Category/List APIs return canonical
 * keys, so this is an exact-key lookup; anything unknown (incl. the backend's
 * "unclassified" fallback) renders grey with an "Unclassified" label.
 */
export const categoryMeta = (vitalKey, subKey) => {
  const meta = VITALS_BY_KEY[vitalKey]?.categories.find((c) => c.key === subKey);
  return meta
    ? { key: meta.key, label: meta.label, color: meta.color }
    : { ...UNCLASSIFIED_META };
};


export const pctLabel = (pct) =>
  pct > 0 && pct < 1 ? "<1%" : `${Math.round(pct)}%`;
